/**
 * TIPOS DE LIMPIEZA
 *
 * Lista por cliente (alta inline) + ficha con pestañas Datos/Requisitos,
 * igual que Clientes/Operarios. El código es visible pero inmutable después
 * del alta: se muestra en un campo deshabilitado, igual que en la edición de
 * clientes y centros. La duración estimada es opcional (null = sin declarar).
 * La baja es activo=false, nunca borrado.
 *
 * REQUISITOS (FASE 3.2B-iv)
 *
 * `tipo_limpieza_requisitos` exige polivalencias del catálogo global con un
 * nivel mínimo (0/25/50/75/100) y si son obligatorias. 0 requisitos
 * obligatorios = cualquier operario asignado es apto por defecto; con N
 * obligatorios, hay que cumplirlos todos — eso lo decide la base
 * (`fn_operario_cumple_tipo`), no esta pantalla. Quitar un requisito no
 * borra la polivalencia del catálogo. Una polivalencia inactiva no se puede
 * elegir para un requisito nuevo (lo impide un trigger), pero un requisito
 * ya existente sobre una polivalencia que se desactivó después se sigue
 * viendo y se puede seguir ajustando: por eso el selector de alta oculta
 * la posibilidad de elegir una inactiva, pero la tabla de requisitos ya
 * creados no oculta ninguno.
 */
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';

import {
  api,
  type Cliente,
  FalloApi,
  type Polivalencia,
  type RequisitoTipo,
  type TipoLimpieza,
} from '../api.ts';
import {
  alternarOrden,
  CabeceraOrdenable,
  Cargando,
  CampoTexto,
  Error as ErrorCaja,
  FiltrosResponsive,
  Panel,
  useDatos,
  Vacio,
} from '../componentes/base.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useSesion } from '../contexto.tsx';
import { useListaUrl } from '../listaUrl.ts';

// -------------------------------------------------------------------
//  PaginaTiposLimpieza — lista por cliente
// -------------------------------------------------------------------

type EstadoFiltroTipo = 'TODAS' | 'ACTIVAS' | 'INACTIVAS';
type OrdenTipos = 'codigo' | 'descripcion' | 'estado';
type Dir = 'asc' | 'desc';

/**
 * `estado: 'TODAS'` porque hoy —antes de esta fase— la lista ya enseña
 * activos e inactivos juntos, sin filtro: excluir inactivos por defecto
 * sería un cambio de comportamiento, no una migración a URL.
 * `codigo ASC` porque es el `ORDER BY codigo` que ya aplicaba el backend.
 */
const DEFAULTS_TIPOS = { q: '', estado: 'TODAS', orden: 'codigo', dir: 'asc' } as const;

export function PaginaTiposLimpieza(): ReactNode {
  const { idCliente = '' } = useParams();
  const navegar = useNavigate();
  const { puede } = useSesion();
  const cliente = useDatos(
    () => api.get<Cliente>(`/api/clientes/${idCliente}`),
    [idCliente],
  );
  const tipos = useDatos(
    () => api.get<{ tiposLimpieza: TipoLimpieza[] }>(`/api/clientes/${idCliente}/tipos-limpieza`),
    [idCliente],
  );

  const [mostrarNuevo, setMostrarNuevo] = useState(false);
  const lista = useListaUrl(DEFAULTS_TIPOS);

  const q = lista.valor('q');
  const filtroEstado = lista.valor('estado') as EstadoFiltroTipo;
  const orden = lista.valor('orden') as OrdenTipos;
  const dir = lista.valor('dir') as Dir;

  function ordenarPor(columna: OrdenTipos): void {
    const siguiente = alternarOrden(columna, orden, dir);
    lista.fijarVarios({ orden: siguiente.orden, dir: siguiente.dir });
  }

  //  FASE 3.3-D3: badge «Filtros (N)» — nunca `q`/`orden`/`dir`.
  const contadorFiltros = filtroEstado !== DEFAULTS_TIPOS.estado ? 1 : 0;

  const filtrados = useMemo(() => {
    const t = q.trim().toLowerCase();
    let l = tipos.datos?.tiposLimpieza ?? [];
    if (filtroEstado === 'ACTIVAS') l = l.filter((x) => x.activo);
    else if (filtroEstado === 'INACTIVAS') l = l.filter((x) => !x.activo);
    if (t !== '') {
      l = l.filter((x) => x.codigo.toLowerCase().includes(t) || x.descripcion.toLowerCase().includes(t));
    }
    const factor = dir === 'asc' ? 1 : -1;
    return [...l].sort((a, b) => {
      if (orden === 'descripcion') return factor * a.descripcion.localeCompare(b.descripcion);
      if (orden === 'estado') return factor * (Number(a.activo) - Number(b.activo));
      return factor * a.codigo.localeCompare(b.codigo);
    });
  }, [tipos.datos, q, filtroEstado, orden, dir]);

  const nombreCliente = cliente.datos?.razonSocial ?? '…';

  return (
    <Shell
      migas={[
        { texto: 'Clientes', a: '/clientes' },
        { texto: nombreCliente, a: `/clientes/${idCliente}` },
        { texto: 'Tipos de limpieza' },
      ]}
      titulo="Tipos de limpieza"
      acciones={
        puede('tipos_limpieza:crear') && !mostrarNuevo ? (
          <button className="principal" onClick={() => setMostrarNuevo(true)}>
            Nuevo tipo
          </button>
        ) : mostrarNuevo ? (
          <button onClick={() => setMostrarNuevo(false)}>Cancelar</button>
        ) : undefined
      }
    >
      {mostrarNuevo && (
        <div className="panel">
          <div className="panel-cabecera">
            <h2>Nuevo tipo de limpieza</h2>
          </div>
          <FormNuevoTipo
            idCliente={idCliente}
            alCrear={() => {
              setMostrarNuevo(false);
              tipos.recargar();
            }}
            alCancelar={() => setMostrarNuevo(false)}
          />
        </div>
      )}

      <div className="panel">
        <Panel estado={tipos}>
          {(d) =>
            d.tiposLimpieza.length === 0 ? (
              <Vacio titulo="Este cliente no tiene tipos de limpieza">
                {puede('tipos_limpieza:crear') && (
                  <p className="suave">Crea el primero con el botón «Nuevo tipo».</p>
                )}
              </Vacio>
            ) : (
              <>
                <FiltrosResponsive
                  contador={contadorFiltros}
                  busqueda={
                    <div className="campo campo-busqueda">
                      <label htmlFor="buscar-tipo-limpieza">Buscar</label>
                      <input
                        id="buscar-tipo-limpieza"
                        type="text"
                        placeholder="Código o descripción…"
                        value={q}
                        onChange={(e) => lista.fijar('q', e.target.value)}
                      />
                    </div>
                  }
                >
                  <div className="campo">
                    <label htmlFor="filtro-estado-tipo-limpieza">Estado</label>
                    <select
                      id="filtro-estado-tipo-limpieza"
                      value={filtroEstado}
                      onChange={(e) => lista.fijar('estado', e.target.value)}
                    >
                      <option value="TODAS">Todas</option>
                      <option value="ACTIVAS">Activas</option>
                      <option value="INACTIVAS">Inactivas</option>
                    </select>
                  </div>
                  {!lista.esPorDefecto && (
                    <button type="button" className="pequeno" onClick={lista.limpiar}>
                      Limpiar filtros
                    </button>
                  )}
                </FiltrosResponsive>

                {filtrados.length === 0 ? (
                  <Vacio titulo="No hay tipos de limpieza que coincidan con los filtros" />
                ) : (
                  <div className="tabla-desliza">
                    <table>
                      <thead>
                        <tr>
                          <CabeceraOrdenable
                            texto="Código"
                            columna="codigo"
                            ordenActual={orden}
                            dirActual={dir}
                            onClick={() => ordenarPor('codigo')}
                          />
                          <CabeceraOrdenable
                            texto="Descripción"
                            columna="descripcion"
                            ordenActual={orden}
                            dirActual={dir}
                            onClick={() => ordenarPor('descripcion')}
                          />
                          <th className="num">Duración (min)</th>
                          <CabeceraOrdenable
                            texto="Estado"
                            columna="estado"
                            ordenActual={orden}
                            dirActual={dir}
                            onClick={() => ordenarPor('estado')}
                          />
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {filtrados.map((t) => (
                          <tr
                            key={t.id}
                            className="pulsable"
                            onClick={() => navegar(`/clientes/${idCliente}/tipos-limpieza/${t.id}`)}
                          >
                            <td className="cod">{t.codigo}</td>
                            <td>{t.descripcion}</td>
                            <td className="num">{t.duracionEstMin ?? '—'}</td>
                            <td>
                              <span className={`etiqueta ${t.activo ? 'exito' : 'neutra'}`}>
                                {t.activo ? 'ACTIVO' : 'INACTIVO'}
                              </span>
                            </td>
                            <td className="derecha">
                              <button className="pequeno">Abrir</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )
          }
        </Panel>
      </div>
    </Shell>
  );
}

// -------------------------------------------------------------------
//  PaginaTipoLimpieza — ficha con pestañas Datos/Requisitos
// -------------------------------------------------------------------

// -------------------------------------------------------------------
//  DisposicionTipoLimpieza — FASE 3.3-C5
//
//  Mismo patrón que `DisposicionCentro`/`DisposicionOperario`: cliente y
//  tipo se cargan una vez aquí (como ya hacía `PaginaTipoLimpieza`, sin
//  cambios de fetch) y viajan por el contexto del outlet.
//
//  `/clientes/:id/tipos-limpieza/:idTipo` (índice) y su `/datos` son la
//  misma pantalla — alias, no redirect — igual que en Operario.
// -------------------------------------------------------------------

type EstadoDatos<T> = ReturnType<typeof useDatos<T>>;

export interface ContextoTipoLimpieza {
  readonly cliente: EstadoDatos<Cliente>;
  readonly tipo: EstadoDatos<TipoLimpieza>;
  readonly idCliente: string;
}

export function useTipoLimpieza(): ContextoTipoLimpieza {
  return useOutletContext<ContextoTipoLimpieza>();
}

const SECCIONES_TIPO = [
  { a: 'datos', texto: 'Datos' },
  { a: 'requisitos', texto: 'Requisitos' },
] as const;

export function DisposicionTipoLimpieza(): ReactNode {
  const { idCliente = '', idTipo = '' } = useParams();
  const location = useLocation();
  const cliente = useDatos(
    () => api.get<Cliente>(`/api/clientes/${idCliente}`),
    [idCliente],
  );
  const tipo = useDatos(
    () => api.get<TipoLimpieza>(`/api/clientes/${idCliente}/tipos-limpieza/${idTipo}`),
    [idCliente, idTipo],
  );

  const nombreCliente = cliente.datos?.razonSocial ?? '…';
  const nombreTipo = tipo.datos !== null ? `${tipo.datos.codigo} — ${tipo.datos.descripcion}` : '…';
  const base = `/clientes/${idCliente}/tipos-limpieza/${idTipo}`;

  function activa(seccion: (typeof SECCIONES_TIPO)[number]['a']): boolean {
    if (seccion === 'datos') return location.pathname === base || location.pathname === `${base}/datos`;
    return location.pathname === `${base}/${seccion}`;
  }

  return (
    <Shell
      migas={[
        { texto: 'Clientes', a: '/clientes' },
        { texto: nombreCliente, a: `/clientes/${idCliente}` },
        { texto: 'Tipos de limpieza', a: `/clientes/${idCliente}/tipos-limpieza` },
        { texto: nombreTipo },
      ]}
      titulo={
        <span className="fila">
          <span>{nombreTipo}</span>
          {tipo.datos !== null && (
            <span className={`etiqueta ${tipo.datos.activo ? 'exito' : 'neutra'}`}>
              {tipo.datos.activo ? 'ACTIVO' : 'INACTIVO'}
            </span>
          )}
        </span>
      }
      tabs={
        <nav className="tabs" aria-label="Secciones del tipo de limpieza">
          {SECCIONES_TIPO.map((s) => (
            //  `Link`, no `NavLink`: ver el comentario equivalente en
            //  `DisposicionOperario` — "Datos" tiene dos rutas válidas (la
            //  base y `/datos`), y el `isActive` interno de `NavLink` no lo
            //  contempla.
            <Link
              key={s.a}
              to={`${base}/${s.a}`}
              className={activa(s.a) ? 'activo' : ''}
              aria-current={activa(s.a) ? 'page' : undefined}
            >
              {s.texto}
            </Link>
          ))}
        </nav>
      }
    >
      <Outlet context={{ cliente, tipo, idCliente } satisfies ContextoTipoLimpieza} />
    </Shell>
  );
}

export function TabDatosTipoLimpieza(): ReactNode {
  const { tipo, idCliente } = useTipoLimpieza();
  const { puede } = useSesion();
  return (
    <div className="panel">
      <Panel estado={tipo}>
        {(d) =>
          puede('tipos_limpieza:editar') ? (
            <FormEditarTipo datos={d} idCliente={idCliente} alEditar={tipo.recargar} />
          ) : (
            <VistaTipoLimpieza datos={d} />
          )
        }
      </Panel>
    </div>
  );
}

export function TabRequisitosTipoLimpieza(): ReactNode {
  const { tipo, idCliente } = useTipoLimpieza();
  const { puede } = useSesion();
  return (
    <div className="panel">
      <Panel estado={tipo}>
        {(d) => (
          <TabRequisitos
            idCliente={idCliente}
            tipo={d}
            puedeGestionar={puede('tipos_limpieza:editar')}
          />
        )}
      </Panel>
    </div>
  );
}

// -------------------------------------------------------------------
//  Vista de solo lectura de datos (sin permiso de edición)
// -------------------------------------------------------------------

function VistaTipoLimpieza({ datos: d }: { datos: TipoLimpieza }): ReactNode {
  return (
    <div className="panel-cuerpo">
      <div className="datos">
        <div className="dato">
          <div className="etiqueta-dato">Código</div>
          <div className="valor-dato mono">{d.codigo}</div>
        </div>
        <div className="dato">
          <div className="etiqueta-dato">Descripción</div>
          <div className="valor-dato">{d.descripcion}</div>
        </div>
        <div className="dato">
          <div className="etiqueta-dato">Duración estimada</div>
          <div className="valor-dato">{d.duracionEstMin === null ? '—' : `${d.duracionEstMin} min`}</div>
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------- formularios

function FormNuevoTipo({
  idCliente,
  alCrear,
  alCancelar,
}: {
  idCliente: string;
  alCrear: () => void;
  alCancelar: () => void;
}): ReactNode {
  const [codigo, setCodigo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [duracionStr, setDuracionStr] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [camposMal, setCamposMal] = useState<Record<string, string>>({});

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    setCamposMal({});
    try {
      const duracionEstMin = parseDuracion(duracionStr);
      if (duracionEstMin === 'invalida') {
        setCamposMal({ duracionEstMin: 'Debe ser un número entero positivo, o dejarlo en blanco.' });
        return;
      }
      await api.post<TipoLimpieza>(`/api/clientes/${idCliente}/tipos-limpieza`, {
        codigo,
        descripcion,
        duracionEstMin,
      });
      alCrear();
    } catch (err) {
      if (err instanceof FalloApi && err.erroresDeCampo != null) setCamposMal(err.erroresDeCampo);
      else setError(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="form-inline" onSubmit={(e) => { void enviar(e); }}>
      <div className="form-grupo">
        <CampoTexto
          label="Código"
          value={codigo}
          onChange={setCodigo}
          error={camposMal['codigo']}
          disabled={enviando}
          required
        />
        <CampoTexto
          label="Descripción"
          value={descripcion}
          onChange={setDescripcion}
          error={camposMal['descripcion']}
          disabled={enviando}
          required
        />
        <CampoTexto
          label="Duración estimada (min)"
          value={duracionStr}
          onChange={setDuracionStr}
          error={camposMal['duracionEstMin']}
          disabled={enviando}
        />
      </div>
      {error !== null && <ErrorCaja error={error} />}
      <div className="fila-botones">
        <button type="submit" className="principal" disabled={enviando}>
          {enviando ? 'Guardando…' : 'Crear tipo'}
        </button>
        <button type="button" onClick={alCancelar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

function FormEditarTipo({
  datos,
  idCliente,
  alEditar,
}: {
  datos: TipoLimpieza;
  idCliente: string;
  alEditar: () => void;
}): ReactNode {
  const idTipo = datos.id;
  const [descripcion, setDescripcion] = useState(datos.descripcion);
  const [duracionStr, setDuracionStr] = useState(datos.duracionEstMin?.toString() ?? '');
  const [activo, setActivo] = useState(datos.activo);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [camposMal, setCamposMal] = useState<Record<string, string>>({});
  const [guardadoOk, setGuardadoOk] = useState(false);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    setCamposMal({});
    setGuardadoOk(false);
    try {
      const duracionEstMin = parseDuracion(duracionStr);
      if (duracionEstMin === 'invalida') {
        setCamposMal({ duracionEstMin: 'Debe ser un número entero positivo, o dejarlo en blanco.' });
        return;
      }
      await api.patch<TipoLimpieza>(`/api/clientes/${idCliente}/tipos-limpieza/${idTipo}`, {
        descripcion,
        duracionEstMin,
        activo,
      });
      setGuardadoOk(true);
      alEditar();
    } catch (err) {
      if (err instanceof FalloApi && err.erroresDeCampo != null) setCamposMal(err.erroresDeCampo);
      else setError(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="form-inline" onSubmit={(e) => { void enviar(e); }}>
      <div className="form-grupo">
        {/* Código es inmutable: se muestra deshabilitado para que sea visible */}
        <div className="campo">
          <label htmlFor="tipo-limpieza-codigo">Código</label>
          <input id="tipo-limpieza-codigo" type="text" value={datos.codigo} disabled />
        </div>
        <CampoTexto
          label="Descripción"
          value={descripcion}
          onChange={setDescripcion}
          error={camposMal['descripcion']}
          disabled={enviando}
          required
        />
        <CampoTexto
          label="Duración estimada (min)"
          value={duracionStr}
          onChange={setDuracionStr}
          error={camposMal['duracionEstMin']}
          disabled={enviando}
        />
      </div>
      <div className="campo" style={{ marginTop: 4 }}>
        <label
          style={{
            textTransform: 'none',
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            color: 'var(--texto)',
            fontWeight: 'normal',
            marginBottom: 0,
          }}
        >
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={activo}
            onChange={(ev) => { setActivo(ev.target.checked); setGuardadoOk(false); }}
            disabled={enviando}
          />
          Tipo activo
        </label>
      </div>
      {error !== null && <ErrorCaja error={error} />}
      {guardadoOk && (
        <div className="nota exito" role="status">
          Cambios guardados. El tipo está <strong>{activo ? 'ACTIVO' : 'INACTIVO'}</strong>.
        </div>
      )}
      <div className="fila-botones">
        <button type="submit" className="principal" disabled={enviando}>
          {enviando ? 'Guardando…' : 'Guardar cambios'}
        </button>
      </div>
    </form>
  );
}

// -------------------------------------------------------------------
//  Tab Requisitos
// -------------------------------------------------------------------

const NIVELES_REQUISITO = [0, 25, 50, 75, 100] as const;

function TabRequisitos({
  idCliente,
  tipo,
  puedeGestionar,
}: {
  idCliente: string;
  tipo: TipoLimpieza;
  puedeGestionar: boolean;
}): ReactNode {
  const requisitos = useDatos(
    () =>
      api.get<{ requisitos: RequisitoTipo[] }>(
        `/api/clientes/${idCliente}/tipos-limpieza/${tipo.id}/requisitos`,
      ),
    [idCliente, tipo.id],
  );
  const [mostrarSelector, setMostrarSelector] = useState(false);
  const [seleccionada, setSeleccionada] = useState<Polivalencia | null>(null);
  const catalogo = useCatalogoPolivalencias(mostrarSelector);

  function cerrarAlta() {
    setMostrarSelector(false);
    setSeleccionada(null);
  }

  return (
    <div className="panel-cuerpo sin-relleno">
      <Panel estado={requisitos}>
        {(d) => (
          <>
            {d.requisitos.length === 0 ? (
              <Vacio titulo="Este tipo de limpieza no exige ninguna polivalencia">
                <p className="suave">
                  Sin requisitos obligatorios, cualquier operario asignado a este tipo es apto por
                  defecto.
                </p>
              </Vacio>
            ) : (
              <div className="tabla-desliza">
                <table>
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Polivalencia</th>
                      <th className="num">Nivel mínimo</th>
                      <th>Obligatorio</th>
                      <th>Estado</th>
                      {puedeGestionar && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {d.requisitos.map((r) => (
                      <FilaRequisito
                        key={r.idPolivalencia}
                        idCliente={idCliente}
                        idTipo={tipo.id}
                        requisito={r}
                        puedeGestionar={puedeGestionar}
                        alCambiar={requisitos.recargar}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {puedeGestionar && (
              <div style={{ marginTop: 16, padding: '0 16px 16px' }}>
                {!mostrarSelector ? (
                  <button onClick={() => setMostrarSelector(true)}>Añadir requisito</button>
                ) : seleccionada === null ? (
                  <>
                    <div className="fila separa" style={{ marginBottom: 4 }}>
                      <h3 style={{ fontSize: 14 }}>Añadir requisito</h3>
                      <button className="pequeno" onClick={cerrarAlta}>
                        Cerrar
                      </button>
                    </div>
                    <PanelCatalogoPolivalencias estado={catalogo}>
                      {(lista) => (
                        <SelectorPolivalencias
                          polivalencias={lista}
                          excluir={new Set(d.requisitos.map((r) => r.idPolivalencia))}
                          onSeleccionar={setSeleccionada}
                        />
                      )}
                    </PanelCatalogoPolivalencias>
                  </>
                ) : (
                  <FormNuevoRequisito
                    idCliente={idCliente}
                    idTipo={tipo.id}
                    polivalencia={seleccionada}
                    alCrear={() => {
                      cerrarAlta();
                      requisitos.recargar();
                    }}
                    alCancelar={() => setSeleccionada(null)}
                  />
                )}
              </div>
            )}
          </>
        )}
      </Panel>
    </div>
  );
}

function FilaRequisito({
  idCliente,
  idTipo,
  requisito,
  puedeGestionar,
  alCambiar,
}: {
  idCliente: string;
  idTipo: string;
  requisito: RequisitoTipo;
  puedeGestionar: boolean;
  alCambiar: () => void;
}): ReactNode {
  const [editando, setEditando] = useState(false);
  const [quitando, setQuitando] = useState(false);

  return (
    <>
      <tr>
        <td className="cod">{requisito.polivalenciaCodigo}</td>
        <td>{requisito.polivalenciaDescripcion}</td>
        <td className="num">{requisito.nivelMinimo} %</td>
        <td>
          <span className={`etiqueta ${requisito.obligatorio ? 'aviso' : 'neutra'}`}>
            {requisito.obligatorio ? 'Obligatorio' : 'No obligatorio'}
          </span>
        </td>
        <td>
          {requisito.polivalenciaActiva ? (
            <span className="etiqueta exito">Activa</span>
          ) : (
            <span className="etiqueta neutra">Inactiva</span>
          )}
        </td>
        {puedeGestionar && (
          <td className="derecha">
            {!editando && !quitando && (
              <>
                <button className="pequeno" onClick={() => setEditando(true)}>
                  Editar
                </button>{' '}
                <button className="pequeno" onClick={() => setQuitando(true)}>
                  Quitar
                </button>
              </>
            )}
          </td>
        )}
      </tr>
      {editando && (
        <tr>
          <td colSpan={puedeGestionar ? 6 : 5}>
            <FormEditarRequisito
              idCliente={idCliente}
              idTipo={idTipo}
              requisito={requisito}
              alGuardar={() => {
                setEditando(false);
                alCambiar();
              }}
              alCancelar={() => setEditando(false)}
            />
          </td>
        </tr>
      )}
      {quitando && (
        <tr>
          <td colSpan={puedeGestionar ? 6 : 5}>
            <ConfirmarQuitarRequisito
              idCliente={idCliente}
              idTipo={idTipo}
              requisito={requisito}
              alQuitar={() => {
                setQuitando(false);
                alCambiar();
              }}
              alCancelar={() => setQuitando(false)}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function FormEditarRequisito({
  idCliente,
  idTipo,
  requisito,
  alGuardar,
  alCancelar,
}: {
  idCliente: string;
  idTipo: string;
  requisito: RequisitoTipo;
  alGuardar: () => void;
  alCancelar: () => void;
}): ReactNode {
  const [nivelMinimo, setNivelMinimo] = useState<number>(requisito.nivelMinimo);
  const [obligatorio, setObligatorio] = useState(requisito.obligatorio);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.patch<RequisitoTipo>(
        `/api/clientes/${idCliente}/tipos-limpieza/${idTipo}/requisitos/${requisito.idPolivalencia}`,
        { nivelMinimo, obligatorio },
      );
      alGuardar();
    } catch (err) {
      setError(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="form-inline" onSubmit={(e) => { void enviar(e); }}>
      <p className="suave" style={{ marginTop: 0 }}>
        {requisito.polivalenciaCodigo} · {requisito.polivalenciaDescripcion}
      </p>
      <div className="form-grupo">
        <div className="campo">
          <label htmlFor={`nivel-req-${requisito.idPolivalencia}`}>Nivel mínimo</label>
          <select
            id={`nivel-req-${requisito.idPolivalencia}`}
            value={nivelMinimo}
            onChange={(e) => setNivelMinimo(Number(e.target.value))}
            disabled={enviando}
          >
            {NIVELES_REQUISITO.map((n) => (
              <option key={n} value={n}>
                {n} %
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="campo" style={{ marginTop: 4 }}>
        <label
          style={{
            textTransform: 'none',
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            color: 'var(--texto)',
            fontWeight: 'normal',
            marginBottom: 0,
          }}
        >
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={obligatorio}
            onChange={(e) => setObligatorio(e.target.checked)}
            disabled={enviando}
          />
          Obligatorio
        </label>
      </div>
      {error !== null && <ErrorCaja error={error} />}
      <div className="fila-botones">
        <button type="submit" className="principal" disabled={enviando}>
          {enviando ? 'Guardando…' : 'Guardar'}
        </button>
        <button type="button" onClick={alCancelar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

function ConfirmarQuitarRequisito({
  idCliente,
  idTipo,
  requisito,
  alQuitar,
  alCancelar,
}: {
  idCliente: string;
  idTipo: string;
  requisito: RequisitoTipo;
  alQuitar: () => void;
  alCancelar: () => void;
}): ReactNode {
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function quitar() {
    setEnviando(true);
    setError(null);
    try {
      await api.del(
        `/api/clientes/${idCliente}/tipos-limpieza/${idTipo}/requisitos/${requisito.idPolivalencia}`,
      );
      alQuitar();
    } catch (err) {
      setError(err);
      setEnviando(false);
    }
  }

  return (
    <div className="nota aviso">
      <p>
        ¿Quitar «{requisito.polivalenciaCodigo} · {requisito.polivalenciaDescripcion}» de los
        requisitos de este tipo? No se borra la polivalencia del catálogo, sólo deja de exigirse
        aquí.
      </p>
      {error !== null && <ErrorCaja error={error} />}
      <div className="fila-botones">
        <button
          className="principal"
          disabled={enviando}
          onClick={() => { void quitar(); }}
        >
          {enviando ? 'Quitando…' : 'Confirmar'}
        </button>
        <button type="button" onClick={alCancelar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </div>
  );
}

function FormNuevoRequisito({
  idCliente,
  idTipo,
  polivalencia,
  alCrear,
  alCancelar,
}: {
  idCliente: string;
  idTipo: string;
  polivalencia: Polivalencia;
  alCrear: () => void;
  alCancelar: () => void;
}): ReactNode {
  const [nivelMinimo, setNivelMinimo] = useState<number>(100);
  const [obligatorio, setObligatorio] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [camposMal, setCamposMal] = useState<Record<string, string>>({});

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    setCamposMal({});
    try {
      await api.post<RequisitoTipo>(`/api/clientes/${idCliente}/tipos-limpieza/${idTipo}/requisitos`, {
        idPolivalencia: polivalencia.id,
        nivelMinimo,
        obligatorio,
      });
      alCrear();
    } catch (err) {
      if (err instanceof FalloApi && err.erroresDeCampo != null) setCamposMal(err.erroresDeCampo);
      else setError(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="form-inline" onSubmit={(e) => { void enviar(e); }}>
      <p className="suave" style={{ marginTop: 0 }}>
        Requisito nuevo: <strong>{polivalencia.codigo} · {polivalencia.descripcion}</strong>
      </p>
      <div className="form-grupo">
        <div className="campo">
          <label htmlFor="nuevo-requisito-nivel">Nivel mínimo</label>
          <select
            id="nuevo-requisito-nivel"
            value={nivelMinimo}
            onChange={(e) => setNivelMinimo(Number(e.target.value))}
            disabled={enviando}
          >
            {NIVELES_REQUISITO.map((n) => (
              <option key={n} value={n}>
                {n} %
              </option>
            ))}
          </select>
          {camposMal['nivelMinimo'] !== undefined && (
            <span className="campo-error">{camposMal['nivelMinimo']}</span>
          )}
        </div>
      </div>
      <div className="campo" style={{ marginTop: 4 }}>
        <label
          style={{
            textTransform: 'none',
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            color: 'var(--texto)',
            fontWeight: 'normal',
            marginBottom: 0,
          }}
        >
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={obligatorio}
            onChange={(e) => setObligatorio(e.target.checked)}
            disabled={enviando}
          />
          Obligatorio
        </label>
        <p className="tenue pequena" style={{ marginTop: 4, marginBottom: 0 }}>
          Los requisitos obligatorios deben cumplirse todos para que el operario sea apto. Los no
          obligatorios no cuentan para la aptitud.
        </p>
      </div>
      {error !== null && <ErrorCaja error={error} />}
      <div className="fila-botones">
        <button type="submit" className="principal" disabled={enviando}>
          {enviando ? 'Guardando…' : 'Añadir requisito'}
        </button>
        <button type="button" onClick={alCancelar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

// -------------------------------------------------------------------
//  Catálogo de polivalencias — carga perezosa (sólo al abrir el selector)
// -------------------------------------------------------------------

type EstadoCatalogoPolivalencias =
  | { fase: 'inactivo' }
  | { fase: 'cargando' }
  | { fase: 'listo'; polivalencias: Polivalencia[] }
  | { fase: 'error'; error: unknown };

function useCatalogoPolivalencias(habilitado: boolean): EstadoCatalogoPolivalencias {
  const [estado, setEstado] = useState<EstadoCatalogoPolivalencias>({ fase: 'inactivo' });

  useEffect(() => {
    if (!habilitado) {
      setEstado({ fase: 'inactivo' });
      return;
    }
    let vivo = true;
    setEstado({ fase: 'cargando' });
    api
      .get<{ polivalencias: Polivalencia[] }>('/api/polivalencias')
      .then((r) => {
        if (vivo) setEstado({ fase: 'listo', polivalencias: r.polivalencias });
      })
      .catch((error: unknown) => {
        if (vivo) setEstado({ fase: 'error', error });
      });
    return () => {
      vivo = false;
    };
  }, [habilitado]);

  return estado;
}

function PanelCatalogoPolivalencias({
  estado,
  children,
}: {
  estado: EstadoCatalogoPolivalencias;
  children: (polivalencias: Polivalencia[]) => ReactNode;
}): ReactNode {
  if (estado.fase === 'inactivo' || estado.fase === 'cargando') return <Cargando />;
  if (estado.fase === 'error') return <ErrorCaja error={estado.error} />;
  return <>{children(estado.polivalencias)}</>;
}

// -------------------------------------------------------------------
//  Buscador de polivalencias — mismo patrón que SelectorCentros
//  (operarios.tsx): buscar por código/descripción, resultados acotados,
//  inactivas visibles pero no seleccionables como requisito nuevo.
// -------------------------------------------------------------------

const LIMITE_RESULTADOS_POLIVALENCIA = 30;

function SelectorPolivalencias({
  polivalencias,
  excluir,
  onSeleccionar,
}: {
  polivalencias: Polivalencia[];
  excluir: Set<string>;
  onSeleccionar: (p: Polivalencia) => void;
}): ReactNode {
  const [busqueda, setBusqueda] = useState('');

  const disponibles = polivalencias.filter((p) => !excluir.has(p.id));
  const t = busqueda.trim().toLowerCase();
  const filtradas =
    t === ''
      ? disponibles
      : disponibles.filter(
          (p) => p.codigo.toLowerCase().includes(t) || p.descripcion.toLowerCase().includes(t),
        );
  const mostradas = filtradas.slice(0, LIMITE_RESULTADOS_POLIVALENCIA);

  return (
    <div className="selector-centros">
      <div className="campo">
        <label htmlFor="buscar-polivalencia-requisito">Buscar polivalencia</label>
        <input
          id="buscar-polivalencia-requisito"
          type="text"
          placeholder="Código o descripción…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          autoFocus
        />
      </div>
      {disponibles.length === 0 ? (
        <p className="suave pequena">No quedan polivalencias disponibles para añadir.</p>
      ) : mostradas.length === 0 ? (
        <p className="suave pequena">Sin resultados para «{busqueda}».</p>
      ) : (
        <>
          <ul className="lista-resultados">
            {mostradas.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  className="resultado-centro"
                  onClick={() => onSeleccionar(p)}
                  disabled={!p.activo}
                  title={p.activo ? undefined : 'Polivalencia inactiva: no se puede exigir en un requisito nuevo.'}
                >
                  <span className="resultado-texto">
                    <strong>
                      {p.descripcion} <span className="mono suave">{p.codigo}</span>
                    </strong>
                    <span className="suave">{p.activo ? 'Activa' : 'Inactiva'}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {filtradas.length > LIMITE_RESULTADOS_POLIVALENCIA && (
            <p className="tenue pequena" style={{ marginTop: 6, marginBottom: 0 }}>
              Mostrando {LIMITE_RESULTADOS_POLIVALENCIA} de {filtradas.length} resultados. Afina la
              búsqueda para encontrar la polivalencia.
            </p>
          )}
        </>
      )}
    </div>
  );
}

// --------------------------------------------------------------- utilidades

/**
 * String vacío → null (sin estimación). Número entero positivo → ese número.
 * Cualquier otra cosa → 'invalida'.
 */
function parseDuracion(str: string): number | null | 'invalida' {
  const s = str.trim();
  if (s === '') return null;
  const n = Number(s);
  if (!Number.isInteger(n) || n <= 0) return 'invalida';
  return n;
}
